import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * Every title that needs (re)measuring, and the one frame callback that will
 * measure them all together.
 *
 * Measuring a title means writing its styles, reading `scrollHeight`, and
 * writing again. Done per element, as it was, each read forces the browser to
 * lay out everything dirtied by the previous element's writes — so appending a
 * page of 24 cards to the collection grid cost 24 synchronous layouts, ~280ms
 * of the ~290ms the append took (profiled on /collections/all, 2026-09-21), and
 * every card that scrolled into view repeated the dance in its own
 * ResizeObserver callback. Batching all the writes, then all the reads, then all
 * the writes turns that into one layout per frame however many titles changed.
 *
 * @type {Set<ProductTitle>}
 */
const pending = new Set();
let scheduled = false;

function flush() {
  scheduled = false;
  const titles = [...pending].filter((title) => title.isConnected);
  pending.clear();
  if (!titles.length) return;

  // Phase 1 — writes: reset every title to its natural flow.
  const jobs = titles.map((title) => title.prepareMeasure()).filter(Boolean);
  // Phase 2 — reads: one layout for all of them.
  for (const job of jobs) job.measure();
  // Phase 3 — writes: clamp the ones that overflow.
  for (const job of jobs) job.apply();
}

/**
 * @param {ProductTitle} title
 */
function schedule(title) {
  pending.add(title);
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(flush);
}

/**
 * One observer for every title on the page. Each title owning its own meant
 * each one got its own callback, and with it its own forced layout.
 * @type {ResizeObserver | undefined}
 */
const resizeObserver =
  'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target instanceof ProductTitle) schedule(entry.target);
        }
      })
    : undefined;

if (!resizeObserver) {
  /** @type {Window} */
  (window).addEventListener('resize', () => {
    for (const title of document.querySelectorAll('product-title')) {
      if (title instanceof ProductTitle) schedule(title);
    }
  });
}

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    // The observer reports the initial size on observe, which schedules the
    // first measurement; a title inside a `content-visibility: auto` card that
    // is not rendered yet reports again once it is, at its real size.
    resizeObserver?.observe(this);
    schedule(this);
  }

  /**
   * The element whose lines are counted.
   * @returns {HTMLElement}
   */
  get #textElement() {
    return this.refs.text || this.querySelector('.title-text') || this;
  }

  /**
   * Resets the title to its natural flow and returns the read and write steps
   * that finish the measurement, so the batch can run all reads before any
   * write. `null` when there is nothing to measure.
   *
   * @returns {{ measure: () => void, apply: () => void } | null}
   */
  prepareMeasure() {
    const textElement = this.#textElement;
    if (!textElement.textContent) return null;

    const configuredMaxLines = Number.parseInt(this.dataset.maxLines || '', 10);
    const maxLines = Number.isFinite(configuredMaxLines) && configuredMaxLines > 0 ? configuredMaxLines : 3;

    textElement.style.display = 'block';
    textElement.style.webkitBoxOrient = '';
    textElement.style.overflow = 'visible';
    textElement.style.textOverflow = '';
    textElement.style.webkitLineClamp = '';

    let needsClamp = false;
    return {
      measure: () => {
        const lineHeight = parseFloat(window.getComputedStyle(this).lineHeight);
        if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;
        const naturalLines = Math.ceil(textElement.scrollHeight / lineHeight);
        needsClamp = naturalLines > maxLines;
      },
      apply: () => {
        if (!needsClamp) {
          textElement.style.display = '';
          textElement.style.webkitBoxOrient = '';
          textElement.style.overflow = '';
          textElement.style.textOverflow = '';
          textElement.style.webkitLineClamp = '';
          return;
        }

        textElement.style.display = '-webkit-box';
        textElement.style.webkitBoxOrient = 'vertical';
        textElement.style.overflow = 'hidden';
        textElement.style.textOverflow = 'ellipsis';
        textElement.style.webkitLineClamp = String(maxLines);
      },
    };
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    resizeObserver?.unobserve(this);
    pending.delete(this);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
